import { Colors, Lightning } from '@lightningjs/sdk';
import { HoverableComponent } from 'components/common/HoverableComponent';
import ToggleListButton from './toggleSection/ToggleListButton';
import { translate } from 'support/translate';
import ActionsList from 'components/common/ActionsList';

interface SettingsToggleSectionTemplateSpec
  extends Lightning.Component.TemplateSpec {
  selectedIndex: number;

  ToggleButtons: typeof ActionsList;
}

const DEFAULT_X = 15;
const DEFAULT_Y = 1;
const ITEM_SPACING = 10;
const BUTTON_FONT_SIZE = 22;
const BUTTONS_WIDTH = 786;
const BUTTON_HEIGHT = 47;

export enum ToggleState {
  ON = 0,
  OFF = 1,
}

export default class SettingsToggleSection extends HoverableComponent<SettingsToggleSectionTemplateSpec> {
  private _selectedIndex = ToggleState.OFF;

  private _ToggleButtons = this.getByRef('ToggleButtons')!;

  get selectedIndex() {
    return this._selectedIndex;
  }

  set selectedIndex(
    selectedIndex: SettingsToggleSectionTemplateSpec['selectedIndex'],
  ) {
    this._selectedIndex =
      selectedIndex === ToggleState.OFF ? ToggleState.OFF : ToggleState.ON;
  }

  static override _template(): Lightning.Component.Template<SettingsToggleSectionTemplateSpec> {
    return {
      rect: true,
      color: Colors('transparent').get(),
      x: DEFAULT_X,
      y: DEFAULT_Y,
      ToggleButtons: {
        flex: { direction: 'column' },
        type: ActionsList,
        spacing: ITEM_SPACING,
        items: [],
        signals: {
          $toggleOn: true,
          $toggleOff: true,
        },
      },
    };
  }

  override _setup() {
    const baseButton = {
      type: ToggleListButton,
      height: BUTTON_HEIGHT,
      minWidth: BUTTONS_WIDTH,
      fontSize: BUTTON_FONT_SIZE,
      backgroundColor: {
        focused: Colors('labelText').get(),
        unfocused: Colors('transparent').get(),
        clicked: Colors('primaryButtonBackground').get(),
      },
      fontColor: {
        focused: Colors('activeText').get(),
        unfocused: Colors('labelText').get(),
        clicked: Colors('primaryButtonTextClicked').get(),
      },
      signals: {
        $onHover: true,
        $onUnhover: true,
      },
    };
    this._ToggleButtons.items = [
      {
        ...baseButton,
        label: translate('global.onButton'),
        action: '$toggleOn',
        passSignals: { $toggleOn: '$toggleOn' },
      },
      {
        ...baseButton,
        label: translate('global.offButton'),
        action: '$toggleOff',
        passSignals: { $toggleOff: '$toggleOff' },
      },
    ];
    this._setSelected(this._selectedIndex);
  }

  private _setSelected(index: number) {
    this._ToggleButtons.children.forEach((child, i) => {
      (child as ToggleListButton).patch({ isSelected: i === index });
    });
    this._selectedIndex = index;
    this.signal('onChanged', this._selectedIndex === 0);
  }

  private $toggleOn() {
    this._setSelected(ToggleState.ON);
  }

  private $toggleOff() {
    this._setSelected(ToggleState.OFF);
  }

  $onHover(target: unknown) {
    switch (target) {
      case this._ToggleButtons:
        this._setState('ToggleButtons');
        break;
    }
  }

  static override _states() {
    return [
      class ToggleButtons extends this {
        override _getFocused() {
          return this._ToggleButtons as Lightning.Component;
        }
      },
    ];
  }
}
